import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { hopeData } from '../../data/mock';
import hopingImage from '@/assets/images/learning-to-pause.png';
import hopeIcon from '@/assets/images/icon-logo.png';
const HopeSection = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-32 bg-[#efede6] relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left - Emotional Image Collage */}
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative">
              {/* Main Image */}
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={hopingImage}
                  alt="Parent reflecting in morning light"
                  className="w-full h-[450px] object-cover grayscale brightness-100"
                />
              </div>

              {/* Overlay Quote */}
              <div className="absolute -bottom-6 -right-6 bg-[#202020] rounded-2xl p-6 shadow-xl max-w-xs">
                <p className="text-[#efede6] text-lg font-light italic mb-2">
                  "I wish to change things but keeping falling into the same pattern."
                </p>
                {/* <p className="text-[#efede6]/50 text-sm">— A peaceful parent</p> */}
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div>
            {/* Icon */}
            <div className={`bg-transparent w-12 h-12 rounded-2xl flex items-center justify-center mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
              <img src={hopeIcon} alt="Hope Icon" className="w-full h-full object-contain" />
            </div>

            {/* Title */}
            <h2 className={`text-3xl md:text-4xl font-normal text-[#202020] mb-8 leading-tight tracking-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {hopeData.title}
            </h2>

            {/* Content */}
            <div className="space-y-5 mb-10">
              {hopeData.content.map((paragraph, index) => (
                <p
                  key={index}
                  className={`text-lg text-[#202020]/60 leading-relaxed transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${(index + 2) * 100}ms` }}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Insight */}
            <div className={`bg-white rounded-2xl p-8 mb-10 shadow-sm transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <h4 className="font-medium text-[#202020] mb-3 text-lg">{hopeData.insight.title}</h4>
              <p className="text-[#202020]/60 leading-relaxed">{hopeData.insight.content}</p>
            </div>

            {/* CTA */}
            <Button
              onClick={() => navigate('/checkout')}
              className={`bg-[#202020] hover:bg-[#303030] text-[#efede6] text-base px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '600ms' }}
            >
              Become a Peaceful Parent
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HopeSection;
